import { Fragment, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TripContent, UserStatus, Activity as ApiActivity, Geo, TransferDetails, Route, ItineraryAccess } from '../../services/api';
import { logger } from '../../utils/logger';
import BookingModal from './BookingModal';
import AddActivityModal, { AddActivityRequest } from './AddActivityModal';
import RouteTimeline from './RouteTimeline';
import ModifyRoutesModal from './ModifyRoutesModal';
import MapToggleButton from './MapToggleButton';
import { TransportIcon } from '../icons/TransportIcons';
import './ItinerarySidebar.css';

// =============================================================================
// TYPES
// =============================================================================

interface ItinerarySidebarProps {
    content: TripContent;
    selectedDay: { cityIdx: number; dayIdx: number };
    sidebarOpen: boolean;
    onClose: () => void;
    onSelectDay: (cityIdx: number, dayIdx: number) => void;
    tripId: string;
    userStatus: UserStatus | null;
    onAddActivity?: (request: AddActivityRequest, dayIndex: number, cityName: string) => Promise<void>;
    onShowUnlockModal?: () => void;
    onClone?: () => void;
    onTripUpdated?: () => void;  // Callback when trip data changes (e.g., route update)
    editToken?: string;          // For anonymous trip editing
    variant?: 'sidebar' | 'inline';  // Mobile: inline (no overlay), Desktop: sidebar
    // Map floating panel integration (desktop only)
    onViewOnMap?: (activityId: string, activityTitle?: string) => void;
    isMapOpen?: boolean;
    onToggleMap?: () => void;
    /** Optional hero block at top of itinerary (desktop). Scrolls away with content. */
    heroSlot?: React.ReactNode;
    onExport?: () => void;
    /** Add a new empty day to the current city. Called when user clicks "+" next to day tabs. */
    onAddDay?: (cityIdx: number) => Promise<void>;
    /** Optional ref to receive openRoutesModal(). Used by TripPage for hero route timeline. */
    openRoutesModalRef?: React.MutableRefObject<(() => void) | null>;
    /** Itinerary access control - for locked day tabs and gating add-day */
    itineraryAccess?: ItineraryAccess;
}

export interface ItinerarySidebarRef {
    openRoutesModal: () => void;
}

interface WeatherData {
    [key: string]: { max: number; min: number };
}

type SystemBlockType =
    | 'SYSTEM_ARRIVAL'
    | 'SYSTEM_DEPARTURE'
    | 'SYSTEM_CHECKIN'
    | 'SYSTEM_CHECKOUT'
    | 'SYSTEM_WAKEUP'
    | 'SYSTEM_RETURN'
    | 'SYSTEM_TRANSFER';

type TransferMode = TransferDetails['mode'];

// Extended Activity type with blockType for type checking
type Activity = ApiActivity;

// =============================================================================
// HELPER FUNCTIONS - System Blocks
// =============================================================================

function isSystemBlock(blockType?: string): blockType is SystemBlockType {
    return !!blockType && blockType.startsWith('SYSTEM_');
}

function getSystemBlockClass(blockType?: string): string {
    if (!isSystemBlock(blockType)) return '';
    const map: Record<SystemBlockType, string> = {
        SYSTEM_ARRIVAL: 'system-arrival',
        SYSTEM_DEPARTURE: 'system-departure',
        SYSTEM_CHECKIN: 'system-checkin',
        SYSTEM_CHECKOUT: 'system-checkout',
        SYSTEM_WAKEUP: 'system-wakeup',
        SYSTEM_RETURN: 'system-return',
        SYSTEM_TRANSFER: 'system-transfer',
    };
    return map[blockType];
}

/** Get transport-specific icon for arrival/departure based on arrivalLocationType */
function getTransportIcon(arrivalLocationType?: string): JSX.Element {
    const iconProps = {
        width: 16,
        height: 16,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };

    switch (arrivalLocationType) {
        case 'CAR':
            // Car icon
            return (
                <svg {...iconProps}>
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2" />
                    <circle cx="7" cy="17" r="2" />
                    <circle cx="17" cy="17" r="2" />
                </svg>
            );
        case 'TRAIN':
            // Train icon
            return (
                <svg {...iconProps}>
                    <rect x="4" y="3" width="16" height="16" rx="2" />
                    <path d="M4 11h16" />
                    <path d="M12 3v8" />
                    <circle cx="8" cy="15" r="1" />
                    <circle cx="16" cy="15" r="1" />
                    <path d="m8 19-2 3" />
                    <path d="m16 19 2 3" />
                </svg>
            );
        case 'BUS':
            // Bus icon
            return (
                <svg {...iconProps}>
                    <path d="M8 6v6" />
                    <path d="M15 6v6" />
                    <path d="M2 12h19.6" />
                    <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5c-.3-1.1-1.3-1.8-2.4-1.8H6c-1.1 0-2.1.7-2.4 1.8l-1.4 5c-.1.4-.2.8-.2 1.2 0 .4.1.8.2 1.2.3 1.1.8 2.8.8 2.8h3" />
                    <circle cx="7" cy="18" r="2" />
                    <circle cx="17" cy="18" r="2" />
                </svg>
            );
        case 'FERRY':
            // Ferry/boat icon
            return (
                <svg {...iconProps}>
                    <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
                    <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8-6-8 6c0 2.2.52 4.3 1.5 6" />
                    <path d="M12 4v4" />
                </svg>
            );
        case 'AIRPORT':
        default:
            // Plane icon (default for AIRPORT or unknown)
            return (
                <svg {...iconProps}>
                    <path d="M2 22h20" />
                    <path d="M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12 5 6l.9-.45a2 2 0 0 1 2.09.2l4.02 3a2 2 0 0 0 2.1.2l4.19-2.06a2.41 2.41 0 0 1 1.73-.17L21 7a1.4 1.4 0 0 1 .87 1.99l-.38.76c-.23.46-.6.84-1.07 1.08L7.58 17.2a2 2 0 0 1-1.22.18Z" />
                </svg>
            );
    }
}

function getSystemBlockIcon(blockType?: string, arrivalLocationType?: string): JSX.Element | null {
    if (!isSystemBlock(blockType)) return null;

    const iconProps = {
        width: 16,
        height: 16,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };

    switch (blockType) {
        case 'SYSTEM_ARRIVAL':
        case 'SYSTEM_DEPARTURE':
            // Use dynamic icon based on transport type
            return getTransportIcon(arrivalLocationType);
        case 'SYSTEM_CHECKIN':
        case 'SYSTEM_CHECKOUT':
            return (
                <svg {...iconProps}>
                    <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z" />
                    <path d="M9 22v-4h6v4" />
                    <path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M12 6h.01" />
                    <path d="M12 10h.01" /><path d="M8 10h.01" /><path d="M16 10h.01" />
                    <path d="M12 14h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" />
                </svg>
            );
        case 'SYSTEM_WAKEUP':
            return (
                <svg {...iconProps}>
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2" /><path d="M12 20v2" />
                    <path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" />
                    <path d="M2 12h2" /><path d="M20 12h2" />
                    <path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
                </svg>
            );
        case 'SYSTEM_RETURN':
            return (
                <svg {...iconProps}>
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
            );
        case 'SYSTEM_TRANSFER':
            // Generic transfer icon (arrows in circle)
            return (
                <svg {...iconProps}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 12h8" />
                    <path d="m12 8 4 4-4 4" />
                </svg>
            );
        default:
            return null;
    }
}

/** Map transferDetails.mode to route-style display type for colors/icons */
function getTransferDisplayType(mode?: TransferMode): 'flight' | 'train' | 'car' | 'bus' | 'ferry' | 'other' {
    switch (mode) {
        case 'boat': return 'ferry';
        case 'bus': return 'bus';
        case 'tram':
        case 'metro': return 'train';
        case 'taxi':
        case 'uber': return 'car';
        default: return 'other';
    }
}

/** Transport type class for SYSTEM_ARRIVAL / SYSTEM_DEPARTURE from route */
function getRouteTransportClass(
    route: Route | undefined,
    blockType: string | undefined,
    isFirstDay: boolean,
    isLastDay: boolean
): string {
    if (!route) return '';
    if (blockType === 'SYSTEM_ARRIVAL' && isFirstDay) {
        const t = route.legs?.[0]?.transport?.type;
        return t && t !== 'unknown' ? ` transport-${t}` : '';
    }
    if (blockType === 'SYSTEM_DEPARTURE' && isLastDay) {
        const t = route.return?.transport?.type;
        return t && t !== 'unknown' ? ` transport-${t}` : '';
    }
    return '';
}

/** Transport type class for SYSTEM_TRANSFER from transferDetails.mode */
function getTransferTransportClass(act: Activity): string {
    if (act.blockType !== 'SYSTEM_TRANSFER' || !act.transferDetails?.mode) return '';
    const displayType = getTransferDisplayType(act.transferDetails.mode);
    return displayType !== 'other' ? ` transport-${displayType}` : '';
}

/** Transport type class from arrivalLocationType (for SYSTEM_ARRIVAL / SYSTEM_DEPARTURE) */
function getArrivalLocationTransportClass(act: Activity): string {
    if (act.blockType !== 'SYSTEM_ARRIVAL' && act.blockType !== 'SYSTEM_DEPARTURE') return '';
    if (!act.arrivalLocationType) return '';
    
    const typeMap: Record<string, string> = {
        'AIRPORT': 'flight',
        'TRAIN': 'train',
        'CAR': 'car',
        'BUS': 'bus',
        'FERRY': 'ferry',
    };
    const transportType = typeMap[act.arrivalLocationType];
    return transportType ? ` transport-${transportType}` : '';
}

/** Route transport type for first leg (arrival) or return leg (departure) */
function getRouteTransportType(
    route: Route | undefined,
    blockType: string | undefined,
    isFirstDay: boolean,
    isLastDay: boolean
): ApiActivity extends { blockType: string } ? import('../../services/api').TransportType | null : null {
    if (!route) return null as any;
    if (blockType === 'SYSTEM_ARRIVAL' && isFirstDay) {
        const t = route.legs?.[0]?.transport?.type;
        return (t && t !== 'unknown' ? t : null) as any;
    }
    if (blockType === 'SYSTEM_DEPARTURE' && isLastDay) {
        const t = route.return?.transport?.type;
        return (t && t !== 'unknown' ? t : null) as any;
    }
    return null as any;
}

/** Transport type for SYSTEM_TRANSFER (for icon); null if 'other' */
function getTransferIconType(act: Activity): import('../../services/api').TransportType | null {
    if (act.blockType !== 'SYSTEM_TRANSFER' || !act.transferDetails?.mode) return null;
    const displayType = getTransferDisplayType(act.transferDetails.mode);
    return displayType !== 'other' ? displayType : null;
}

/**
 * Get icon for specific transfer mode
 */
function getTransferModeIcon(mode?: TransferMode): JSX.Element {
    const iconProps = {
        width: 14,
        height: 14,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 2,
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
    };

    switch (mode) {
        case 'metro':
            return (
                <svg {...iconProps}>
                    <rect x="6" y="2" width="12" height="16" rx="2" />
                    <line x1="9" y1="18" x2="9" y2="22" />
                    <line x1="15" y1="18" x2="15" y2="22" />
                    <circle cx="9" cy="14" r="1" />
                    <circle cx="15" cy="14" r="1" />
                    <line x1="6" y1="8" x2="18" y2="8" />
                </svg>
            );
        case 'bus':
            return (
                <svg {...iconProps}>
                    <path d="M8 6v6" /><path d="M16 6v6" />
                    <path d="M2 12h20" />
                    <path d="M5 18h14a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3Z" />
                    <circle cx="7" cy="16" r="2" />
                    <circle cx="17" cy="16" r="2" />
                </svg>
            );
        case 'taxi':
        case 'uber':
            return (
                <svg {...iconProps}>
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
                    <circle cx="7" cy="17" r="2" />
                    <circle cx="17" cy="17" r="2" />
                </svg>
            );
        case 'tram':
            return (
                <svg {...iconProps}>
                    <rect x="4" y="3" width="16" height="16" rx="2" />
                    <path d="M4 11h16" />
                    <path d="M12 3v8" />
                    <path d="m8 19-2 3" />
                    <path d="m18 22-2-3" />
                    <circle cx="8" cy="15" r="1" fill="currentColor" />
                    <circle cx="16" cy="15" r="1" fill="currentColor" />
                </svg>
            );
        case 'boat':
            return (
                <svg {...iconProps}>
                    <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
                    <path d="M12 18V3" />
                    <path d="m4 15 8 3 8-3" />
                    <path d="M4 12 12 9l8 3" />
                </svg>
            );
        case 'cable_car':
            return (
                <svg {...iconProps}>
                    <path d="M10 3h.01" /><path d="M14 2h.01" />
                    <path d="m2 9 20-5" />
                    <path d="M12 12v5" />
                    <rect x="6" y="17" width="12" height="5" rx="1" />
                </svg>
            );
        case 'walking':
            return (
                <svg {...iconProps}>
                    <circle cx="12" cy="5" r="1" />
                    <path d="m9 20 3-6 3 6" />
                    <path d="M6 8h6l2 4" />
                </svg>
            );
        default:
            return (
                <svg {...iconProps}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 12h8" />
                    <path d="m12 8 4 4-4 4" />
                </svg>
            );
    }
}

// =============================================================================
// HELPER FUNCTIONS - Travel Estimates
// =============================================================================

const MAX_WALK_METERS = 3000;
const MAX_WALK_MINUTES = 45;

function filterTravelForDisplay(
    route: { duration: number; distance: number } | undefined,
    mode: 'walking' | 'driving'
): { duration: number; distance: number } | null {
    if (!route) return null;
    const { duration, distance } = route;

    if (distance === 0 || duration === 0) return null;

    if (mode === 'walking') {
        if (distance > MAX_WALK_METERS || duration > MAX_WALK_MINUTES) return null;
    }

    return route;
}

function shouldShowTravel(activity: Activity): boolean {
    const travel = activity.travelToNext;
    if (!travel) return false;
    return (
        filterTravelForDisplay(travel.walking, 'walking') !== null ||
        filterTravelForDisplay(travel.driving, 'driving') !== null ||
        !!travel.transit
    );
}

// =============================================================================
// HELPER FUNCTIONS - Location
// =============================================================================

/**
 * Check if activity has valid geo coordinates
 * 
 * RULES:
 * - pending status = never valid (no coords yet)
 * - null coords = never valid
 * - (0,0) = never valid (Null Island bug)
 */
function hasValidGeo(geo?: Geo): boolean {
    if (!geo) return false;
    if (geo.status === 'pending') return false;
    // Handle null coords (new schema: nullable lat/lng)
    if (geo.lat === null || geo.lng === null) return false;
    if (typeof geo.lat !== 'number' || typeof geo.lng !== 'number') return false;
    // Null island check - (0,0) is always invalid
    if (geo.lat === 0 && geo.lng === 0) return false;
    return true;
}

/**
 * Check if activity location is approximate (low confidence)
 */
function isLocationApproximate(activity: Activity): boolean {
    // Legacy support: check old confidenceScore
    if (activity.confidenceScore === 'estimate') return true;
    // New geo-based check
    if (activity.geo?.confidence === 'low') return true;
    if (activity.geo?.status === 'approximate') return true;
    return false;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function ItinerarySidebar({
    content,
    selectedDay,
    sidebarOpen,
    onClose,
    onSelectDay,
    tripId,
    userStatus,
    onAddActivity,
    onShowUnlockModal,
    onClone,
    onTripUpdated,
    editToken,
    variant = 'sidebar',
    onViewOnMap,
    isMapOpen = false,
    onToggleMap,
    heroSlot,
    onAddDay,
    openRoutesModalRef,
    itineraryAccess,
}: ItinerarySidebarProps) {
    const isInline = variant === 'inline';
    const { t, i18n } = useTranslation();
    const [bookingActivity, setBookingActivity] = useState<{ query: string; title: string } | null>(null);
    const [weather, setWeather] = useState<WeatherData>({});
    const [showAddActivityModal, setShowAddActivityModal] = useState(false);
    const [addDayLoading, setAddDayLoading] = useState(false);
    const [showCloneConfirm, setShowCloneConfirm] = useState(false);
    const [isAddingActivity, setIsAddingActivity] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [hideSystemBlocks, setHideSystemBlocks] = useState(false);
    const [showRoutesModal, setShowRoutesModal] = useState(false);
    const [lastDayPerCity, setLastDayPerCity] = useState<Record<number, number>>({});
    const [tripComModal, setTripComModal] = useState<{
        open: boolean;
        type: 'hotels' | 'flights' | 'carhire';
        city: string;
        checkin: string;
        checkout: string;
        flightDate: string;
    }>({ open: false, type: 'hotels', city: '', checkin: '', checkout: '', flightDate: '' });
    useEffect(() => {
        if (openRoutesModalRef) openRoutesModalRef.current = () => setShowRoutesModal(true);
        return () => { if (openRoutesModalRef) openRoutesModalRef.current = null; };
    }, [openRoutesModalRef]);
    const dayTabsRef = useRef<HTMLDivElement>(null);
    const filtersRef = useRef<HTMLDivElement>(null);
    
    // Track last viewed day per city for RouteTimeline
    useEffect(() => {
        setLastDayPerCity(prev => ({
            ...prev,
            [selectedDay.cityIdx]: selectedDay.dayIdx
        }));
    }, [selectedDay]);

    // =========================================================================
    // Click Outside Handler for Filters
    // =========================================================================
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
                setShowFilters(false);
            }
        };
        if (showFilters) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showFilters]);

    // =========================================================================
    // Weather Fetch
    // =========================================================================
    useEffect(() => {
        const fetchAllWeather = async () => {
            const weatherResults: WeatherData = {};

            for (let cIdx = 0; cIdx < content.cities.length; cIdx++) {
                const city = content.cities[cIdx];
                if (!city.days.length) continue;

                const firstDay = city.days[0];
                const lastDay = city.days[city.days.length - 1];
                const firstActivity = city.days[0].activities[0];
                // Use geo coordinates (resolved from geocoder)
                if (!firstActivity?.geo || !hasValidGeo(firstActivity.geo)) continue;

                try {
                    const { lat, lng } = firstActivity.geo;
                    const response = await fetch(
                        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min&start_date=${firstDay.date}&end_date=${lastDay.date}&timezone=auto`
                    );
                    const data = await response.json();

                    if (data.daily) {
                        city.days.forEach((_, dIdx) => {
                            weatherResults[`${cIdx}-${dIdx}`] = {
                                max: Math.round(data.daily.temperature_2m_max[dIdx]),
                                min: Math.round(data.daily.temperature_2m_min[dIdx]),
                            };
                        });
                    }
                } catch (err) {
                    logger.error('Weather fetch failed for', city.name, err);
                }
            }
            setWeather(weatherResults);
        };

        if (content) fetchAllWeather();
    }, [content]);

    // =========================================================================
    // Scroll selected day tab into view
    // =========================================================================
    useEffect(() => {
        if (dayTabsRef.current) {
            const activeTab = dayTabsRef.current.querySelector('.day-tab.active');
            if (activeTab) {
                activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [selectedDay]);

    // =========================================================================
    // Helpers
    // =========================================================================
    
    // Get days count per city - use itineraryAccess.daysPerCity if available (for locked tabs)
    const getDaysInCity = (cityIdx: number): number => {
        if (itineraryAccess?.daysPerCity && itineraryAccess.daysPerCity[cityIdx] !== undefined) {
            return itineraryAccess.daysPerCity[cityIdx];
        }
        return content.cities[cityIdx]?.days?.length || 0;
    };
    
    // Calculate global day number (1-based) using itineraryAccess if available
    const getDayNumber = (cityIdx: number, dayIdx: number) => {
        let num = 1;
        for (let c = 0; c < cityIdx; c++) {
            num += getDaysInCity(c);
        }
        return num + dayIdx;
    };
    
    // Check if a specific day is locked (not visible due to access restriction)
    const isDayLocked = (cityIdx: number, dayIdx: number): boolean => {
        if (!itineraryAccess || !itineraryAccess.isRestricted) return false;
        
        // Calculate global day index (0-based)
        let globalDayIndex = 0;
        for (let c = 0; c < cityIdx; c++) {
            globalDayIndex += getDaysInCity(c);
        }
        globalDayIndex += dayIdx;
        
        // Day is locked if its global index >= daysVisible
        return globalDayIndex >= itineraryAccess.daysVisible;
    };

    const formatShortDate = (dateStr: string) => {
        try {
            const parts = dateStr.split('-');
            const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            const locale = i18n.language === 'ro' ? 'ro-RO' : 'en-US';
            return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date).toUpperCase();
        } catch {
            return dateStr;
        }
    };

    // =========================================================================
    // System Block CTA – Trip.com search box (iframe) + fallback links
    // =========================================================================
    const TRIP_COM_AFFILIATE = 'Allianceid=7759788&SID=291667629&trip_sub1=';
    const TRIP_COM_HOTELS_BASE = `https://www.trip.com/partners/ad/S11380868?${TRIP_COM_AFFILIATE}`;
    const TRIP_COM_FLIGHTS_BASE = `https://www.trip.com/partners/ad/S11380875?${TRIP_COM_AFFILIATE}`;
    const TRIP_COM_CARHIRE_URL = 'https://www.trip.com/carhire/?Allianceid=7759788&SID=291667629&trip_sub1=&trip_sub3=D11381050';

    const getTripComIframeSrc = (type: 'hotels' | 'flights' | 'carhire', city: string, checkin: string, checkout: string, flightDate: string) => {
        if (type === 'carhire') return TRIP_COM_CARHIRE_URL;
        const base = type === 'hotels' ? TRIP_COM_HOTELS_BASE : TRIP_COM_FLIGHTS_BASE;
        const params = new URLSearchParams();
        params.set('city', city);
        params.set('destination', city);
        if (type === 'hotels') {
            params.set('checkin', checkin);
            params.set('checkout', checkout);
        } else {
            params.set('date', flightDate);
        }
        return `${base}&${params.toString()}`;
    };

    const getSystemBlockCTA = (blockType: string | undefined, cityName: string, arrivalLocationType?: string) => {
        if (!isSystemBlock(blockType)) return null;

        const destination = encodeURIComponent(cityName);

        switch (blockType) {
            case 'SYSTEM_ARRIVAL':
                // Only show flight CTA if arriving by plane
                if (arrivalLocationType && arrivalLocationType !== 'AIRPORT') {
                    // For car/train/bus arrivals, no CTA needed
                    return null;
                }
                return {
                    label: t('trip.find_flights', 'Find Flights'),
                    url: `https://www.skyscanner.net/transport/flights/anywhere/${destination}/${content.dateRange.start.replace(/-/g, '')}/`,
                    icon: 'plane' as const,
                    openTripComModal: () => setTripComModal({
                        open: true,
                        type: 'flights',
                        city: cityName,
                        checkin: content.dateRange.start,
                        checkout: content.dateRange.end,
                        flightDate: content.dateRange.start,
                    }),
                };
            case 'SYSTEM_DEPARTURE':
                // Only show flight CTA if departing by plane
                if (arrivalLocationType && arrivalLocationType !== 'AIRPORT') {
                    return null;
                }
                return {
                    label: t('trip.find_return_flights', 'Find Return Flights'),
                    url: `https://www.skyscanner.net/transport/flights/${destination}/anywhere/${content.dateRange.end.replace(/-/g, '')}/`,
                    icon: 'plane' as const,
                    openTripComModal: () => setTripComModal({
                        open: true,
                        type: 'flights',
                        city: cityName,
                        checkin: content.dateRange.start,
                        checkout: content.dateRange.end,
                        flightDate: content.dateRange.end,
                    }),
                };
            case 'SYSTEM_CHECKIN':
                return {
                    label: t('trip.find_hotels', 'Find Hotels'),
                    url: `https://www.google.com/travel/hotels?q=hotels%20${destination}&dates=${content.dateRange.start},${content.dateRange.end}`,
                    icon: 'hotel' as const,
                    openTripComModal: () => setTripComModal({
                        open: true,
                        type: 'hotels',
                        city: cityName,
                        checkin: content.dateRange.start,
                        checkout: content.dateRange.end,
                        flightDate: content.dateRange.start,
                    }),
                };
            default:
                return null;
        }
    };

    // =========================================================================
    // Current selections
    // =========================================================================
    const currentCity = content.cities[selectedDay.cityIdx];
    const currentDay = currentCity?.days[selectedDay.dayIdx];
    const isFirstDayOfTrip = selectedDay.cityIdx === 0 && selectedDay.dayIdx === 0;
    const lastCityIdx = content.cities.length - 1;
    const lastCityDays = content.cities[lastCityIdx]?.days.length;
    const lastDayIdx = lastCityDays != null ? lastCityDays - 1 : 0;
    const isLastDayOfTrip = selectedDay.cityIdx === lastCityIdx && selectedDay.dayIdx === lastDayIdx;

    // =========================================================================
    // Render
    // =========================================================================
    // Show RouteTimeline only on mobile (inline); on desktop it lives in TripHero
    const showRouteTimeline = isInline && content.cities.length > 1;

    const mainContent = (
        <>
            {/* Route Timeline - only for multi-city trips on desktop, always on mobile */}
            {showRouteTimeline && (
                <RouteTimeline
                        content={content}
                        selectedCityIdx={selectedDay.cityIdx}
                        lastDayPerCity={lastDayPerCity}
                        onSelectCity={(cityIdx, dayIdx) => onSelectDay(cityIdx, dayIdx)}
                        onEditRoutes={() => setShowRoutesModal(true)}
                        isEditable={userStatus?.isOwner || !!editToken}
                    />
                )}

                {/* City Title + Toolbar (Filters + Map Toggle) */}
                <div className="city-header">
                    <h2 className="city-title">{currentCity?.name}</h2>
                    <div className="city-header-actions">
                        {/* Map Toggle Button (desktop only) */}
                        {onToggleMap && (
                            <MapToggleButton
                                isMapOpen={isMapOpen}
                                onClick={onToggleMap}
                            />
                        )}

                        {/* Filters */}
                        <div className="filters-wrapper" ref={filtersRef}>
                            <button 
                                className={`filters-btn ${showFilters ? 'active' : ''} ${hideSystemBlocks ? 'has-active-filter' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                                    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                                    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                                    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
                                    <line x1="17" y1="16" x2="23" y2="16" />
                                </svg>
                                {t('trip.filters', 'Filters')}
                                {hideSystemBlocks && <span className="filter-badge">1</span>}
                            </button>

                            {showFilters && (
                                <div className="filters-dropdown">
                                    <label className="filter-toggle">
                                        <input 
                                            type="checkbox" 
                                            checked={hideSystemBlocks}
                                            onChange={(e) => setHideSystemBlocks(e.target.checked)}
                                        />
                                        <span className="toggle-switch" />
                                        <div className="toggle-label">
                                            <span>{t('trip.filter_hide_system', 'Hide system blocks')}</span>
                                            <span className="filter-hint">{t('trip.filter_hide_system_hint', '(Arrival, Check-in, etc.)')}</span>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Day Tabs with Weather + Add Day */}
                <div className="day-tabs-container" ref={dayTabsRef}>
                    {/* Generate day tabs based on daysPerCity (from itineraryAccess) or actual content */}
                    {Array.from({ length: getDaysInCity(selectedDay.cityIdx) }).map((_, dayIdx) => {
                        const dayNum = getDayNumber(selectedDay.cityIdx, dayIdx);
                        const isActive = dayIdx === selectedDay.dayIdx;
                        const dayWeather = weather[`${selectedDay.cityIdx}-${dayIdx}`];
                        const locked = isDayLocked(selectedDay.cityIdx, dayIdx);
                        
                        // Get the actual day content if available (for date and weather)
                        const dayContent = currentCity?.days?.[dayIdx];

                        if (locked) {
                            // Locked day tab - show lock icon, click opens unlock modal
                            return (
                                <button
                                    key={`locked-${dayIdx}`}
                                    className={`day-tab day-tab-locked ${isActive ? 'active' : ''}`}
                                    onClick={() => onShowUnlockModal?.()}
                                    title={t('trip.unlock_to_view', 'Unlock to view this day')}
                                >
                                    <span className="day-tab-lock-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                        </svg>
                                    </span>
                                    <span className="day-tab-num">{t('trip.day', 'Day')} {dayNum}</span>
                                </button>
                            );
                        }

                        return (
                            <button
                                key={dayContent?.date || `day-${dayIdx}`}
                                className={`day-tab ${isActive ? 'active' : ''}`}
                                onClick={() => onSelectDay(selectedDay.cityIdx, dayIdx)}
                            >
                                {dayContent?.date && (
                                    <span className="day-tab-date">{formatShortDate(dayContent.date)}</span>
                                )}
                                <span className="day-tab-num">{t('trip.day', 'Day')} {dayNum}</span>
                                {dayWeather && (
                                    <span className="day-tab-weather">
                                        {dayWeather.max}° / {dayWeather.min}°
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {/* Add Day button - only show if user has canAddDay permission */}
                    {onAddDay && (userStatus?.isOwner || editToken) && (itineraryAccess?.canAddDay !== false) && (
                        <button
                            type="button"
                            className="add-day-btn"
                            onClick={async () => {
                                if (addDayLoading || !currentCity) return;
                                setAddDayLoading(true);
                                try {
                                    await onAddDay(selectedDay.cityIdx);
                                } finally {
                                    setAddDayLoading(false);
                                }
                            }}
                            disabled={addDayLoading}
                            title={t('trip.add_day', 'Add day')}
                            aria-label={t('trip.add_day', 'Add day')}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </button>
                    )}
                    {/* Show locked Add Day button that opens unlock modal when canAddDay is false */}
                    {onAddDay && (userStatus?.isOwner || editToken) && (itineraryAccess?.canAddDay === false) && (
                        <button
                            type="button"
                            className="add-day-btn add-day-btn-locked"
                            onClick={() => onShowUnlockModal?.()}
                            title={t('trip.unlock_to_add_day', 'Unlock to add days')}
                            aria-label={t('trip.unlock_to_add_day', 'Unlock to add days')}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </button>
                    )}
                </div>

                {/* Day Section Header - title + AI description above activities list */}
                {currentDay && (
                    <div className="day-section-header">
                        <h3 className="day-theme">{(currentDay as any).theme || t('trip.day_activities', 'Day Activities')}</h3>
                        <p className="day-description">
                            {(currentDay as any).description || t('trip.day_description_placeholder')}
                        </p>
                    </div>
                )}

                {/* Activities List or Empty Day message */}
                <div className="activities-list">
                    {currentDay && currentDay.activities.length === 0 ? (
                        <div className="empty-day-message">
                            <p className="empty-day-text">{t('trip.empty_day_message', 'Raisa will help you build the itinerary for this day.')}</p>
                            <p className="empty-day-hint">{t('trip.empty_day_hint', 'Use the chat to tell Raisa what you’d like to do.')}</p>
                        </div>
                    ) : currentDay
                        ? currentDay.activities
                            .filter(act => !hideSystemBlocks || !isSystemBlock((act as Activity).blockType))
                            .map((activity, actIdx) => {
                        const act = activity as Activity;
                        const systemBlock = isSystemBlock(act.blockType);
                        const blockClass = getSystemBlockClass(act.blockType);
                        const blockIcon = getSystemBlockIcon(act.blockType, act.arrivalLocationType);
                        const cta = getSystemBlockCTA(act.blockType, currentCity.name, act.arrivalLocationType);
                        const routeTransportClass = getRouteTransportClass(content.route, act.blockType, isFirstDayOfTrip, isLastDayOfTrip);
                        const transferTransportClass = getTransferTransportClass(act);
                        const arrivalLocationClass = getArrivalLocationTransportClass(act);
                        const transportClass = routeTransportClass || arrivalLocationClass || transferTransportClass;
                        const routeTransportType = getRouteTransportType(content.route, act.blockType, isFirstDayOfTrip, isLastDayOfTrip);
                        const transferIconType = getTransferIconType(act);
                        const transportIconType = routeTransportType || transferIconType;

                        return (
                            <Fragment key={activity.id}>
                                <div className={`activity-card ${blockClass}${transportClass}`}>
                                    {/* Activity Number / System Icon / Transport Icon */}
                                    <div className={`activity-number ${systemBlock ? 'system' : ''}`}>
                                        {systemBlock && transportIconType
                                            ? <TransportIcon type={transportIconType} size={16} />
                                            : systemBlock && blockIcon
                                                ? blockIcon
                                                : actIdx + 1}
                                    </div>

                                    {/* Activity Content */}
                                    <div className="activity-content">
                                        {/* Time */}
                                        <div className="activity-meta">
                                            <span className="activity-time">{activity.time}</span>
                                        </div>

                                        {/* Title */}
                                        <h4 className="activity-title">{activity.title}</h4>

                                        {/* Description */}
                                        {activity.description && (
                                            <p className="activity-desc">{activity.description}</p>
                                        )}

                                        {/* Transfer Details (for SYSTEM_TRANSFER blocks) */}
                                        {act.blockType === 'SYSTEM_TRANSFER' && act.transferDetails && (
                                            <div className="transfer-details">
                                                <div className="transfer-route">
                                                    <span className="transfer-from">{act.transferDetails.from}</span>
                                                    <span className="transfer-arrow">→</span>
                                                    <span className="transfer-to">{act.transferDetails.to}</span>
                                                </div>
                                                <div className="transfer-info">
                                                    <span className="transfer-mode">
                                                        {getTransferModeIcon(act.transferDetails.mode)}
                                                        {t(`trip.transfer_mode_${act.transferDetails.mode}`, act.transferDetails.mode)}
                                                    </span>
                                                    {act.transferDetails.line && (
                                                        <span className="transfer-line">{act.transferDetails.line}</span>
                                                    )}
                                                    {act.transferDetails.durationMin && (
                                                        <span className="transfer-duration">{act.transferDetails.durationMin} min</span>
                                                    )}
                                                    {act.transferDetails.cost && (
                                                        <span className="transfer-cost">{act.transferDetails.cost}</span>
                                                    )}
                                                </div>
                                                {act.transferDetails.notes && (
                                                    <div className="transfer-notes">{act.transferDetails.notes}</div>
                                                )}
                                            </div>
                                        )}

                                        {/* Location with approximate badge (not for transfers) */}
                                        {act.blockType !== 'SYSTEM_TRANSFER' && (
                                            <div className="activity-location">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                    <circle cx="12" cy="10" r="3" />
                                                </svg>
                                                <span>{activity.location?.name || currentCity.name}</span>
                                                {isLocationApproximate(act) && (
                                                    <span className="location-approximate-badge" title={t('trip.location_approximate', 'Approximate location')}>
                                                        ~
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Action buttons row */}
                                        <div className="activity-actions">
                                            {/* Ticket button (non-system blocks) */}
                                            {activity.requiresTicket && !systemBlock && (
                                                <button
                                                    className="affiliate-btn ticket-btn"
                                                    onClick={() => setBookingActivity({
                                                        query: activity.location?.name || activity.title,
                                                        title: activity.title,
                                                    })}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                                                        <line x1="1" y1="10" x2="23" y2="10" />
                                                    </svg>
                                                    {t('trip.buy_ticket', 'Buy Ticket')}
                                                </button>
                                            )}

                                            {/* View on map button (desktop only, activities with valid geo) */}
                                            {onViewOnMap && hasValidGeo(act.geo) && !isLocationApproximate(act) && (
                                                <button
                                                    className="view-on-map-btn"
                                                    onClick={() => onViewOnMap(activity.id, activity.title)}
                                                    title={t('trip.view_on_map')}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                                                        <line x1="8" y1="2" x2="8" y2="18" />
                                                        <line x1="16" y1="6" x2="16" y2="22" />
                                                    </svg>
                                                    <span className="view-on-map-text">{t('trip.view_on_map')}</span>
                                                </button>
                                            )}
                                        </div>

                                        {/* System Block CTA – opens Trip.com search box modal with pre-filled params */}
                                        {cta && (
                                            'openTripComModal' in cta && cta.openTripComModal ? (
                                                <button
                                                    type="button"
                                                    className="system-block-cta"
                                                    onClick={cta.openTripComModal}
                                                >
                                                    {cta.icon === 'plane' && (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2Z" />
                                                        </svg>
                                                    )}
                                                    {cta.icon === 'hotel' && (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z" />
                                                            <path d="M9 22v-4h6v4" />
                                                        </svg>
                                                    )}
                                                    {cta.label}
                                                </button>
                                            ) : (
                                                <a
                                                    href={cta.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="system-block-cta"
                                                >
                                                    {cta.icon === 'plane' && (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2Z" />
                                                        </svg>
                                                    )}
                                                    {cta.icon === 'hotel' && (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z" />
                                                            <path d="M9 22v-4h6v4" />
                                                        </svg>
                                                    )}
                                                    {cta.label}
                                                </a>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Travel Estimate between activities */}
                                {actIdx < currentDay.activities.length - 1 && shouldShowTravel(act) && (
                                    <div className="travel-estimate">
                                        <div className="travel-items">
                                            {filterTravelForDisplay(act.travelToNext?.walking, 'walking') && (
                                                <div className="travel-estimate-item" title={t('trip.walking', 'Walking')}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="5" r="1" />
                                                        <path d="m9 20 3-6 3 6" />
                                                        <path d="M6 8h6l2 4" />
                                                    </svg>
                                                    <span className="travel-duration">{act.travelToNext!.walking!.duration}min</span>
                                                    <span className="travel-distance">
                                                        {Math.round(act.travelToNext!.walking!.distance / 10) / 100}km
                                                    </span>
                                                </div>
                                            )}
                                            {filterTravelForDisplay(act.travelToNext?.driving, 'driving') && (
                                                <div className="travel-estimate-item" title={t('trip.driving', 'Driving')}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
                                                        <circle cx="7" cy="17" r="2" />
                                                        <circle cx="17" cy="17" r="2" />
                                                    </svg>
                                                    <span className="travel-duration">{act.travelToNext!.driving!.duration}min</span>
                                                </div>
                                            )}
                                            {act.travelToNext?.transit && (
                                                <div className="travel-estimate-item" title={t('trip.transit', 'Public Transit')}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="6" y="2" width="12" height="16" rx="2" />
                                                        <line x1="9" y1="18" x2="9" y2="22" />
                                                        <line x1="15" y1="18" x2="15" y2="22" />
                                                        <circle cx="9" cy="14" r="1" />
                                                        <circle cx="15" cy="14" r="1" />
                                                        <line x1="6" y1="8" x2="18" y2="8" />
                                                    </svg>
                                                    <span className="travel-duration">{act.travelToNext!.transit!.duration}min</span>
                                                    {act.travelToNext!.transit!.isEstimate && (
                                                        <span className="estimate-tag">{t('trip.estimated_by_ai', 'AI estimate')}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Fragment>
                        );
                    }) : null}

                    {/* Add Activity Button */}
                    <button 
                        className="add-activity-btn"
                        onClick={() => {
                            // Check if user is authenticated before opening modal
                            if (!userStatus?.isAuthenticated) {
                                onShowUnlockModal?.();
                                return;
                            }
                            // Check if user is the owner - if not, ask if they want to clone
                            if (!userStatus?.isOwner) {
                                setShowCloneConfirm(true);
                                return;
                            }
                            setShowAddActivityModal(true);
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        {t('trip.add_activity', 'Add Activity')}
                    </button>
                </div>
        </>
    );

    return (
        <>
            <aside className={`itinerary-sidebar ${sidebarOpen ? 'open' : ''} ${isInline ? 'inline' : ''} ${heroSlot ? 'has-hero' : ''}`}>
                {heroSlot ? (
                    <div className="itinerary-scroll-view">
                        {heroSlot}
                        {mainContent}
                    </div>
                ) : (
                    mainContent
                )}
                {/* Mobile Close Button - only show in sidebar mode */}
                {!isInline && (
                    <button className="close-sidebar-mobile" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </aside>

            <BookingModal
                isOpen={!!bookingActivity}
                onClose={() => setBookingActivity(null)}
                query={bookingActivity?.query || ''}
                title={bookingActivity?.title || ''}
            />

            <AddActivityModal
                isOpen={showAddActivityModal}
                onClose={() => setShowAddActivityModal(false)}
                tripId={tripId}
                dayIndex={selectedDay.dayIdx}
                cityName={currentCity?.name || ''}
                existingActivities={currentDay?.activities.map(a => ({
                    id: a.id,
                    time: a.time,
                    title: a.title,
                })) || []}
                onAddActivity={async (request) => {
                    if (!onAddActivity) return;
                    setIsAddingActivity(true);
                    try {
                        await onAddActivity(request, selectedDay.dayIdx, currentCity?.name || '');
                        setShowAddActivityModal(false);
                    } finally {
                        setIsAddingActivity(false);
                    }
                }}
                isLoading={isAddingActivity}
            />

            {/* Trip.com Search Box Modal – iframe with pre-filled destination & dates */}
            {tripComModal.open && (
                <div
                    className="tripcom-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={tripComModal.type === 'hotels' ? t('trip.find_hotels', 'Find Hotels') : t('trip.find_flights', 'Find Flights')}
                >
                    <div className="tripcom-modal">
                        <button
                            type="button"
                            className="tripcom-modal-close"
                            onClick={() => setTripComModal(prev => ({ ...prev, open: false }))}
                            aria-label={t('trip.close', 'Close')}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                        <iframe
                            title={tripComModal.type === 'hotels' ? t('trip.find_hotels', 'Find Hotels') : t('trip.find_flights', 'Find Flights')}
                            src={getTripComIframeSrc(
                                tripComModal.type,
                                tripComModal.city,
                                tripComModal.checkin,
                                tripComModal.checkout,
                                tripComModal.flightDate
                            )}
                            className="tripcom-iframe"
                            style={{
                                width: '320px',
                                height: tripComModal.type === 'flights' ? '320px' : '480px',
                                border: 'none',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Clone Confirmation Modal */}
            {showCloneConfirm && (
                <div className="clone-confirm-overlay" onClick={() => setShowCloneConfirm(false)}>
                    <div className="clone-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="clone-confirm-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.5">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        </div>
                        <h3>{t('trip.clone_confirm_title', 'Clone this itinerary?')}</h3>
                        <p>{t('trip.clone_confirm_desc', 'This is a shared itinerary. To make changes, you need to create your own copy.')}</p>
                        <div className="clone-confirm-actions">
                            <button 
                                className="clone-confirm-cancel"
                                onClick={() => setShowCloneConfirm(false)}
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button 
                                className="clone-confirm-btn"
                                onClick={() => {
                                    setShowCloneConfirm(false);
                                    onClone?.();
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                                {t('trip.clone_confirm_btn', 'Clone & Edit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modify Routes Modal */}
            <ModifyRoutesModal
                isOpen={showRoutesModal}
                onClose={() => setShowRoutesModal(false)}
                content={content}
                tripId={tripId}
                editToken={editToken}
                onRouteUpdated={() => {
                    setShowRoutesModal(false);
                    onTripUpdated?.();
                }}
            />
        </>
    );
}
